import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiFetch } from './api';

function ContattiSolleciti({ onSelectionChange = () => {} }) {
  // Stati per i dati
  const [contatti, setContatti] = useState([]);
  const [filteredContatti, setFilteredContatti] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Stati per i filtri
  const [searchRagioneSociale, setSearchRagioneSociale] = useState('');
  const [searchComune, setSearchComune] = useState('');
  const [searchProvincia, setSearchProvincia] = useState('');
  const [searchRegione, setSearchRegione] = useState('');
  const [searchTipo, setSearchTipo] = useState('');
  const [searchRecapito, setSearchRecapito] = useState('');
  const [filterPolizza, setFilterPolizza] = useState('');
  const [filterAnni, setFilterAnni] = useState('');
  
  // 🔧 STATI PER ORDINAMENTO
  const [sortField, setSortField] = useState('ragioneSociale'); // Default: Ente ASC
  const [sortDirection, setSortDirection] = useState('asc');

  // Stati per la selezione
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [processingSelection, setProcessingSelection] = useState(false);

  // Placeholders MEMOIZZATI - FISSI
  const placeholders = useMemo(() => [
    '{CODICE_TESSERA}',
    '{DATA_INIZIO}', 
    '{TIPO}',
    '{RAGIONE_SOCIALE}',
    '{COMUNE}',
    '{SIGLAPROV}',
    '{REGIONE}',
    '{RECAPITO}',
    '{ANNI}',
    '{DEBITI}',
    '{TOTALE}',
    '{POLIZZA}',
    '{DATA_ODIERNA}',
    '{IMMAGINE_INTESTAZIONE}',
    '{IMMAGINE_FONDO}',
    '{FIRMA_ANUTEL}'
  ], []);

  // Funzione di validazione email semplice
  const isValidEmail = useCallback((email) => {
    return email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  }, []);

  // 🔧 FUNZIONE ORDINAMENTO - CORRETTA POSIZIONE
  const handleSort = useCallback((field) => {
    setSortField(prevField => {
      setSortDirection(prevDirection => {
        if (prevField === field) {
          return prevDirection === 'asc' ? 'desc' : 'asc';
        } else {
          return 'asc';
        }
      });
      return field;
    });
  }, []);

  // 🔧 FUNZIONE ICONE ORDINAMENTO
  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <i className="fas fa-sort text-muted ms-1"></i>;
    }
    return sortDirection === 'asc' ? 
      <i className="fas fa-sort-up text-primary ms-1"></i> : 
      <i className="fas fa-sort-down text-primary ms-1"></i>;
  };

  // 🔧 ELABORAZIONE DEBITI E TOTALE - FUNZIONE SEPARATA
  const processDebiti = useCallback((debitiString) => {
    if (!debitiString || debitiString.trim() === '') {
      return { debitiFormatted: '', totaleCalculated: 0, totaleFormatted: '0,00 €' };
    }

    const cleaned = debitiString.trim();

    // Sia i separatori tra importi multipli sia il separatore decimale usano ','.
    // Gli importi in valuta hanno sempre esattamente 2 cifre decimali, quindi ogni
    // coppia (parte-intera, 2-cifre) costituisce un singolo importo da sommare.
    // Es: "100,50,1.700,00" → rimuovi punti → "100,50,1700,00"
    //     → coppie (100,50)=100.50 + (1700,00)=1700.00 → totale 1800.50
    const noDots = cleaned.replace(/\./g, '');
    const parts = noDots.split(',').map(p => p.trim()).filter(p => p !== '');

    let totaleCalculated = 0;
    let i = 0;
    while (i < parts.length) {
      const intPart = parts[i];
      if (i + 1 < parts.length && /^\d{2}$/.test(parts[i + 1])) {
        totaleCalculated += parseFloat(`${intPart}.${parts[i + 1]}`) || 0;
        i += 2;
      } else {
        totaleCalculated += parseFloat(intPart) || 0;
        i += 1;
      }
    }

    const debitiFormatted = `${cleaned} €`;

    const totaleFormatted = new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(totaleCalculated);

    return {
      debitiFormatted,
      totaleCalculated,
      totaleFormatted
    };
  }, []);

  // Caricamento dati - CALLBACK MEMOIZZATO
  const loadContatti = useCallback(async () => {
    setLoading(true);
    setError('');
    setContatti([]);
    setFilteredContatti([]);
    setSelectedRows([]);
    
    try {
      console.log('🔄 Caricamento contatti solleciti...');
      const response = await apiFetch('/api/Contact/solleciti');
      
      if (response.ok) {
        const data = await response.json();
        console.log('📊 Dati solleciti ricevuti:', data);
        
        // Trasforma i dati in formato compatibile con il sistema
        const contattiConId = (data || []).map((item, index) => {
          // Gestione email dal campo RECAPITO
          const emailArray = item.RECAPITO ? 
            item.RECAPITO.split(';').map(e => e.trim()).filter(e => isValidEmail(e)) : 
            [];

          // 🔧 PROCESSA DEBITI USANDO LA FUNZIONE SEPARATA
          const debitiProcessed = processDebiti(item.DEBITI);

          return {
            id: `sollecito_${index}_${Date.now()}`, // ID univoco
            key: `sollecito_${item.CODICE_TESSERA}_${index}_${Date.now()}`,
            
            // Campi originali con nomi standardizzati
            codiceTessera: item.CODICE_TESSERA || '',
            dataInizio: item.DATA_INIZIO || '',
            tipo: item.TIPO || '',
            ragioneSociale: item.RAGIONE_SOCIALE || '',
            comune: item.COMUNE || '',
            siglaProv: item.SIGLAPROV || '',
            regione: item.REGIONE || '',
            recapito: item.RECAPITO || '',
            anni: item.ANNI || '',
            
            // 🔧 DEBITI E TOTALE ELABORATI
            debiti: item.DEBITI || '', // Valore originale
            debitiFormatted: debitiProcessed.debitiFormatted, // Per placeholder {DEBITI}
            totale: item.TOTALE || '', // Valore originale (spesso vuoto)
            totaleCalculated: debitiProcessed.totaleCalculated, // Valore numerico calcolato
            totaleFormatted: debitiProcessed.totaleFormatted, // Per placeholder {TOTALE}
            
            polizza: item.POLIZZA || 'NO',
            
            // Campi per compatibilità con sistema email
            email: emailArray.length > 0 ? emailArray[0] : '', // Prima email come principale
            emailOrd: emailArray, // Tutte le email come ordinarie
            emailPec: [], // Nessuna PEC specifica nei solleciti
            
            // Metadati
            hasTessera: false,
            fileUrl: ''
          };
        });
        
        setContatti(contattiConId);
        setFilteredContatti(contattiConId);
        
        console.log(`✅ Caricati ${contattiConId.length} contatti solleciti`);
      } else {
        const errorText = await response.text();
        throw new Error(`Errore ${response.status}: ${errorText}`);
      }
    } catch (err) {
      console.error('❌ Errore caricamento contatti solleciti:', err);
      setError(`Errore nel caricamento: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [isValidEmail, processDebiti]);

  // Carica contatti al mount del componente
  useEffect(() => {
    loadContatti();
  }, [loadContatti]);

  // 🔧 APPLICA FILTRI E ORDINAMENTO - CALLBACK MEMOIZZATO
  const applyFilters = useCallback(() => {
    setProcessingSelection(true);
    
    setTimeout(() => {
      let filtered = [...contatti];
      
      // FILTRI
      if (searchRagioneSociale.trim()) {
        filtered = filtered.filter(c => 
          c.ragioneSociale && c.ragioneSociale.toLowerCase().includes(searchRagioneSociale.toLowerCase())
        );
      }
      
      if (searchComune.trim()) {
        filtered = filtered.filter(c => 
          c.comune && c.comune.toLowerCase().includes(searchComune.toLowerCase())
        );
      }
      
      if (searchProvincia.trim()) {
        filtered = filtered.filter(c => 
          c.siglaProv && c.siglaProv.toLowerCase().includes(searchProvincia.toLowerCase())
        );
      }
      
      if (searchRegione.trim()) {
        filtered = filtered.filter(c => 
          c.regione && c.regione.toLowerCase().includes(searchRegione.toLowerCase())
        );
      }
      
      if (searchTipo.trim()) {
        filtered = filtered.filter(c => 
          c.tipo && c.tipo.toLowerCase().includes(searchTipo.toLowerCase())
        );
      }
      
      if (searchRecapito.trim()) {
        filtered = filtered.filter(c => 
          c.recapito && c.recapito.toLowerCase().includes(searchRecapito.toLowerCase())
        );
      }
      
      if (filterPolizza) {
        filtered = filtered.filter(c => c.polizza === filterPolizza);
      }
      
      // Filtro per anni
      if (filterAnni) {
        const currentYear = new Date().getFullYear().toString();
        filtered = filtered.filter(c => {
          if (!c.anni) return false;
          
          const anni = c.anni.split(',').map(anno => anno.trim());
          
          if (filterAnni === 'solo_corrente') {
            return anni.length === 1 && anni[0] === currentYear;
          } else if (filterAnni === 'piu_anni') {
            return anni.length > 1 || (anni.length === 1 && anni[0] !== currentYear);
          }
          return true;
        });
      }
      
      // 🔧 ORDINAMENTO
      if (sortField) {
        filtered.sort((a, b) => {
          let valueA = a[sortField];
          let valueB = b[sortField];
          
          // Gestione valori nulli
          if (valueA == null) valueA = '';
          if (valueB == null) valueB = '';
          
          // 🔧 ORDINAMENTO SPECIFICO PER TIPO CAMPO
          if (sortField === 'totaleCalculated') {
            // Ordinamento numerico per totale debiti
            valueA = parseFloat(valueA) || 0;
            valueB = parseFloat(valueB) || 0;
          } else if (sortField === 'anni') {
            // Ordinamento per numero di anni (conta le virgole)
            valueA = String(valueA).split(',').length;
            valueB = String(valueB).split(',').length;
          } else {
            // Ordinamento alfabetico standard
            valueA = String(valueA).toLowerCase();
            valueB = String(valueB).toLowerCase();
          }
          
          // Confronto finale
          if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
          if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
          return 0;
        });
      }

      setFilteredContatti(filtered);
      setSelectedRows([]);
      setSelectAll(false);
      setProcessingSelection(false);
    }, 0);
  }, [contatti, searchRagioneSociale, searchComune, searchProvincia, searchRegione, searchTipo, searchRecapito, filterPolizza, filterAnni, sortField, sortDirection]);

  // Applica filtri quando cambiano
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Gestione selezione - CALLBACK MEMOIZZATI
  const handleRowSelection = useCallback((contatto, isSelected) => {
    setSelectedRows(prev => {
      let newSelected;
      if (isSelected) {
        newSelected = [...prev, contatto];
      } else {
        newSelected = prev.filter(r => r.id !== contatto.id);
      }
      return newSelected;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setProcessingSelection(true);
    
    setTimeout(() => {
      setSelectAll(prev => {
        const newSelectAll = !prev;
        if (newSelectAll) {
          setSelectedRows([...filteredContatti]);
        } else {
          setSelectedRows([]);
        }
        setProcessingSelection(false);
        return newSelectAll;
      });
    }, 0);
  }, [filteredContatti]);

  // Aggiorna selectAll quando cambiano le selezioni
  useEffect(() => {
    setSelectAll(selectedRows.length === filteredContatti.length && filteredContatti.length > 0);
  }, [selectedRows.length, filteredContatti.length]);

  // COMUNICAZIONE CON PARENT - MEMOIZZATO CON DIPENDENZE STABILI
  useEffect(() => {
    const emailCount = selectedRows.reduce((total, row) => {
      return total + (row.emailOrd?.length || 0) + (row.emailPec?.length || 0);
    }, 0);

    const selectionData = {
      selectedRows: selectedRows,
      destinatariCount: selectedRows.length,
      emailCount,
      placeholders: placeholders
    };

    console.log('📬 ContattiSolleciti - Comunicazione con parent:', selectionData);
    onSelectionChange(selectionData);

  }, [selectedRows, placeholders, onSelectionChange]);

  // Reset filtri
  const resetFilters = useCallback(() => {
    setSearchRagioneSociale('');
    setSearchComune('');
    setSearchProvincia('');
    setSearchRegione('');
    setSearchTipo('');
    setSearchRecapito('');
    setFilterPolizza('');
    setFilterAnni('');
  }, []);

  // Contatori
  const totalEmails = filteredContatti.reduce((total, row) => 
    total + (row.emailOrd?.length || 0) + (row.emailPec?.length || 0), 0
  );

  return (
    <div className="card border-primary">
      <div className="card-header bg-light">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0 text-primary">
            <i className="fas fa-exclamation-triangle"></i> Contatti Solleciti
          </h5>
          <div className="d-flex align-items-center">
            {selectedRows.length > 0 && (
              <span className="badge bg-success me-2">
                <i className="fas fa-check me-1"></i>
                {selectedRows.length} selezionati
              </span>
            )}
            <span className="badge bg-info me-2">
              <i className="fas fa-list me-1"></i>
              {filteredContatti.length} elementi
            </span>
            <span className="badge bg-primary">
              <i className="fas fa-envelope me-1"></i>
              {totalEmails} email
            </span>
          </div>
        </div>
      </div>
      
      <div className="card-body">
        {/* Filtri di ricerca */}
        <div className="row mb-2">
          <div className="col-md-2">
            <label className="form-label fw-bold text-start w-100">
              <i className="fas fa-building me-1 text-muted"></i> Tipo Ente:
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Filtra per tipo..."
              value={searchTipo}
              onChange={(e) => setSearchTipo(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label fw-bold text-start w-100">
              <i className="fas fa-university me-1 text-muted"></i> Ente:
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Ragione sociale..."
              value={searchRagioneSociale}
              onChange={(e) => setSearchRagioneSociale(e.target.value)}
            />
          </div>
          <div className="col-md-2">
            <label className="form-label fw-bold text-start w-100">
              <i className="fas fa-map-marker-alt me-1 text-muted"></i> Comune:
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Comune..."
              value={searchComune}
              onChange={(e) => setSearchComune(e.target.value)}
            />
          </div>
          <div className="col-md-1">
            <label className="form-label fw-bold text-start w-100">
              <i className="fas fa-map me-1 text-muted"></i> Pr:
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="PR"
              value={searchProvincia}
              onChange={(e) => setSearchProvincia(e.target.value)}
            />
          </div>
          <div className="col-md-2">
            <label className="form-label fw-bold text-start w-100">
              <i className="fas fa-shield-alt me-1 text-muted"></i> Polizza:
            </label>
            <select 
              className="form-select form-select-sm"
              value={filterPolizza}
              onChange={(e) => setFilterPolizza(e.target.value)}
            >
              <option value="">Tutti</option>
              <option value="SI">SI</option>
              <option value="NO">NO</option>
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label fw-bold text-start w-100">
              <i className="fas fa-calendar-alt me-1 text-muted"></i> Anni:
            </label>
            <select 
              className="form-select form-select-sm"
              value={filterAnni}
              onChange={(e) => setFilterAnni(e.target.value)}
            >
              <option value="">Tutti</option>
              <option value="solo_corrente">Solo {new Date().getFullYear()}</option>
              <option value="piu_anni">Più anni</option>
            </select>
          </div>
        </div>
        
        <div className="row mb-3">
          <div className="col-md-4">
            <label className="form-label fw-bold text-start w-100">
              <i className="fas fa-globe me-1 text-muted"></i> Regione:
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Regione..."
              value={searchRegione}
              onChange={(e) => setSearchRegione(e.target.value)}
            />
          </div>
          <div className="col-md-8">
            <label className="form-label fw-bold text-start w-100">
              <i className="fas fa-envelope me-1 text-muted"></i> Email:
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Filtra per email..."
              value={searchRecapito}
              onChange={(e) => setSearchRecapito(e.target.value)}
            />
          </div>
        </div>

        {/* Stato caricamento/errori */}
        {loading && (
          <div className="text-center py-4">
            <div className="spinner-border text-primary me-2" role="status"></div>
            <span>Caricamento contatti solleciti...</span>
          </div>
        )}

        {error && (
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </div>
        )}

        {/* 🔧 TABELLA DATI CON ORDINAMENTO */}
        {!loading && !error && (
          <div className="table-responsive position-relative" style={{ maxHeight: 500, overflowY: 'auto' }}>
            {processingSelection && (
              <div className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center bg-white bg-opacity-75" style={{zIndex:10}}>
                <i className="fas fa-spinner fa-spin fa-2x text-primary mb-2"></i>
                <div className="fw-bold">Aggiornamento selezione...</div>
              </div>
            )}
            
            <table className="table table-sm table-hover mb-0">
              <thead className="table-dark sticky-top">
                <tr>
                  <th style={{width: '50px'}}>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={selectAll}
                        ref={el => { 
                          if (el) el.indeterminate = selectedRows.length > 0 && selectedRows.length < filteredContatti.length; 
                        }}
                        onChange={handleSelectAll}
                        disabled={filteredContatti.length === 0}
                      />
                    </div>
                  </th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleSort('codiceTessera')}>
                    Tessera {getSortIcon('codiceTessera')}
                  </th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleSort('tipo')}>
                    Tipo {getSortIcon('tipo')}
                  </th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleSort('ragioneSociale')}>
                    Ente {getSortIcon('ragioneSociale')}
                  </th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleSort('comune')}>
                    Comune {getSortIcon('comune')}
                  </th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleSort('siglaProv')}>
                    PR {getSortIcon('siglaProv')}
                  </th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleSort('regione')}>
                    Regione {getSortIcon('regione')}
                  </th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleSort('anni')}>
                    Anni {getSortIcon('anni')}
                  </th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleSort('totaleCalculated')}>
                    Debiti € {getSortIcon('totaleCalculated')}
                  </th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleSort('polizza')}>
                    Polizza {getSortIcon('polizza')}
                  </th>
                  <th style={{cursor: 'pointer'}} onClick={() => handleSort('recapito')}>
                    Email {getSortIcon('recapito')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredContatti.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="text-center py-4 text-muted">
                      <i className="fas fa-inbox me-2"></i>
                      {loading ? 'Caricamento in corso...' : 'Nessun sollecito trovato'}
                    </td>
                  </tr>
                ) : (
                  filteredContatti.map((contatto) => (
                    <tr key={contatto.id}>
                      <td>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={selectedRows.some(r => r.id === contatto.id)}
                            onChange={(e) => handleRowSelection(contatto, e.target.checked)}
                          />
                        </div>
                      </td>
                      <td>
                        <span className="badge bg-warning text-dark">
                          <i className="fas fa-id-card me-1"></i>
                          {contatto.codiceTessera}
                        </span>
                      </td>
                      <td>
                        <span className="badge bg-secondary">{contatto.tipo}</span>
                      </td>
                      <td className="fw-bold" style={{maxWidth: '150px'}}>
                        <div className="text-truncate" title={contatto.ragioneSociale}>
                          {contatto.ragioneSociale}
                        </div>
                      </td>
                      <td>{contatto.comune}</td>
                      <td>
                        <span className="badge bg-info">{contatto.siglaProv}</span>
                      </td>
                      <td>
                        <small className="text-muted">{contatto.regione}</small>
                      </td>
                      <td>
                        <small className="text-primary fw-bold">
                          {contatto.anni && contatto.anni.includes(new Date().getFullYear().toString()) ? (
                            <span className="badge bg-success me-1">{new Date().getFullYear()}</span>
                          ) : null}
                          {contatto.anni}
                        </small>
                      </td>
                      <td>
                        <small className="text-danger fw-bold" title={`Totale: ${contatto.totaleFormatted}`}>
                          {contatto.debitiFormatted || contatto.debiti}
                        </small>
                      </td>
                      <td>
                        <span className={`badge ${contatto.polizza === 'SI' ? 'bg-success' : 'bg-danger'}`}>
                          <i className={`fas ${contatto.polizza === 'SI' ? 'fa-check' : 'fa-times'} me-1`}></i>
                          {contatto.polizza}
                        </span>
                      </td>
                      <td style={{maxWidth: '200px'}}>
                        {contatto.emailOrd?.length > 0 ? (
                          <div>
                            <span className="badge bg-primary me-1">{contatto.emailOrd.length}</span>
                            <small className="text-truncate d-block" title={contatto.recapito}>
                              <i className="fas fa-envelope text-primary me-1"></i>
                              {contatto.recapito}
                            </small>
                          </div>
                        ) : (
                          <span className="text-muted">
                            <i className="fas fa-envelope-slash me-1"></i>
                            Nessuna email
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Info selezione */}
        {filteredContatti.length > 0 && (
          <div className="d-flex justify-content-between align-items-center mt-3">
            <small className="text-muted">
              <i className="fas fa-info-circle me-1"></i>
              Visualizzati {filteredContatti.length} di {contatti.length} elementi
            </small>
            {selectedRows.length > 0 && (
              <small className="text-success">
                <i className="fas fa-check-circle me-1"></i>
                {selectedRows.length} elementi selezionati - {totalEmails} email da inviare
              </small>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ContattiSolleciti;